import type { PreOrder as OfflinePreOrder } from "@stockline/shared/types/orders/pre-order";
import type { PreOrderUpdate } from "@/app/types";
import { legacyToOfflinePatch } from "@/query/hooks/usePreOrders";

/**
 * Pending header-write registry.
 *
 * Header edits on a pre-order (clearing the comment, …) are dispatched through
 * `useUpdatePreOrder`, which writes Dexie, enqueues on the offline change queue,
 * and pushes fire-and-forget. The confirm/export gesture rides a SEPARATE,
 * direct push-batch transport. If the queued header edit has not reached the
 * server when confirm fires, the server exports the stale value and its
 * response repaints it locally — the just-cleared comment reappears.
 *
 * This registry lets the confirm gesture carry the freshest dispatched header
 * value INTO the confirm push-batch, so the server applies it in the SAME
 * transaction as the export. There is no cross-transport ordering left to lose:
 * every header dispatch records its intended (snake_case) patch here, keyed by
 * pre-order id; the confirm path folds it into `preOrderUpdate` and clears it
 * once the confirm has committed. The parallel change-queue push stays as an
 * idempotent durable backstop.
 */
export type HeaderPatch = Record<string, unknown>;

const pending = new Map<string, HeaderPatch>();

/** Merge a dispatched header patch into the registry (latest value wins per key). */
export function recordPendingHeaderWrite(
  preOrderId: string,
  patch: HeaderPatch,
): void {
  const existing = pending.get(preOrderId);
  pending.set(preOrderId, existing ? { ...existing, ...patch } : { ...patch });
}

/** Drop the registered patch — call once the confirm has committed. */
export function clearPendingHeaderWrite(preOrderId: string): void {
  pending.delete(preOrderId);
}

/**
 * Header fields that must never ride the confirm transaction:
 *  - `preOrderMetadata` would clobber server-stamped export state
 *    (`export.status`, `export.reference`, …);
 *  - `status` is owned by the confirm itself.
 * Every other known header field is safe to carry.
 */
const CONFIRM_EXCLUDED_FIELDS: (keyof OfflinePreOrder)[] = [
  "preOrderMetadata",
  "status",
];

/**
 * Offline `preOrderUpdate` to fold into a confirm push-batch, or `undefined`
 * when there is nothing safe to carry.
 *
 * Translates the recorded (snake_case) patch into the offline shape and drops
 * the confirm-unsafe fields above. `legacyToOfflinePatch` only maps KNOWN
 * header keys, so it also bounds what can be folded — any field the recap
 * dispatches through this registry (comment today; `delivery_date`,
 * `partner_id`, … tomorrow) rides the confirm without a per-field change here,
 * and an unmapped key is simply ignored rather than silently dropped from a
 * hand-maintained allowlist. An emptied comment is preserved as `""` (a clear),
 * not `null`, by `legacyToOfflinePatch`.
 */
export function buildConfirmHeaderUpdate(
  preOrderId: string,
): Partial<OfflinePreOrder> | undefined {
  const patch = pending.get(preOrderId);
  if (!patch) return undefined;
  const update = legacyToOfflinePatch(patch as PreOrderUpdate);
  for (const field of CONFIRM_EXCLUDED_FIELDS) delete update[field];
  return Object.keys(update).length > 0 ? update : undefined;
}
